/**
 * Trainer session detail — view session metadata, write clinical notes,
 * see consent log + events. (Visible to admin too.)
 */
import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Mast } from "../components/Mast.js";
import { api, API_BASE } from "../lib/api.js";

interface Session {
  id: string; case_id: string; trainer_id: string;
  scheduled_for: string; estimated_min: number;
  protocol_hint: string | null;
  state: string;
  started_at: string | null; ended_at: string | null;
  recording_r2_key: string | null; metric_summary: string | null;
  case_name: string;
  case_user_id: string | null;
  join_url: string | null;
  join_expires_at: string | null;
}
interface Note {
  id: number; trainer_id: string; note_md: string;
  visible_to_case: number; created_at: string;
}
interface Event {
  id: number; ts: string;
  actor: "trainer" | "case" | "system";
  type: string; payload: string | null;
}

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();
  const [s, setS]       = useState<Session | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft]     = useState("");
  const [visibleToCase, setVisibleToCase] = useState(true);
  const [posting, setPosting] = useState(false);
  const [err, setErr]         = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const [sessionRes, eventsRes] = await Promise.all([
      api<{ ok: true; session: Session; notes: Note[] }>(`/api/sessions/${id}`),
      api<{ ok: true; events: Event[] }>(`/api/sessions/${id}/events`),
    ]);
    setS(sessionRes.session);
    setNotes(sessionRes.notes);
    setEvents(eventsRes.events);
    setLoading(false);
  }
  useEffect(() => { if (id) load(); }, [id]);

  async function postNote(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setPosting(true); setErr(null);
    try {
      await api(`/api/sessions/${id}/notes`, { method: "POST", body: {
        note_md: draft.trim(),
        visible_to_case: visibleToCase,
      }});
      setDraft("");
      await load();
    } catch (e: any) {
      setErr(e?.body?.error ?? e?.message ?? "Failed");
    } finally { setPosting(false); }
  }

  if (loading || !s) return <><Mast section="Session 詳細" /><main className="page muted">載入中…</main></>;

  const proto = s.protocol_hint ? safeParse(s.protocol_hint) : null;

  return (
    <>
      <Mast section={`Session · ${s.case_name}`} />
      <main className="page">
        <Link to={`/dashboard/cases/${s.case_id}`} style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", display: "inline-block", marginBottom: "var(--s-4)" }}>
          ← 回個案 {s.case_name}
        </Link>

        <section style={{ marginBottom: "var(--s-7)" }}>
          <div className="serial rise rise-1">SESSION · {s.id.slice(0, 12)}</div>
          <h1 className="rise rise-2" style={{ marginTop: "var(--s-2)" }}>
            <span className="italic-display">{s.case_name}</span>{" "}<span className="muted" style={{ fontSize: "var(--t-md)" }}>· {fmtFull(s.scheduled_for)}</span>
          </h1>
          <div className="row rise rise-3" style={{ marginTop: "var(--s-3)", gap: "var(--s-4)" }}>
            <StateChip state={s.state} />
            <span className="muted tabular">{s.estimated_min} 分</span>
            {s.started_at && <span className="muted tabular">started {fmtTime(s.started_at)}</span>}
            {s.ended_at   && <span className="muted tabular">ended   {fmtTime(s.ended_at)}</span>}
          </div>

          <div className="row rise rise-4" style={{ marginTop: "var(--s-5)", gap: "var(--s-3)" }}>
            {(s.state === "planned" || s.state === "lobby" || s.state === "live" || s.state === "paused") &&
              <Link to={`/studio/${s.id}`} className="btn btn--rust" style={{ textDecoration: "none" }}>進入 Studio →</Link>}
          </div>

          {/* JOIN URL — visible while session is bookable. Trainer may
              need to re-share the link (different device, lost browser
              session, etc.) even after the case has activated, so the
              card stays up until the session ends or the token expires. */}
          {s.join_url && s.state !== "ended" && s.state !== "aborted" && (
            <JoinUrlCard url={s.join_url} expiresAt={s.join_expires_at} activated={!!s.case_user_id} />
          )}
        </section>

        <section style={{ marginBottom: "var(--s-7)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-5)" }}>
          <div className="surface rise rise-5" style={{ padding: "var(--s-5)" }}>
            <div className="serial" style={{ marginBottom: "var(--s-3)" }}>Protocol hint</div>
            {proto ? (
              <div className="kv">
                <span className="kv__key">Source</span><span className="kv__val">{proto.source ?? "—"}</span>
                <span className="kv__key">Game</span><span className="kv__val">{proto.game ?? "—"}</span>
                {proto.reward && <><span className="kv__key">Reward</span><span className="kv__val tabular">{proto.reward}</span></>}
              </div>
            ) : <div className="muted">沒設 protocol</div>}
          </div>

          <div className="surface rise rise-6" style={{ padding: "var(--s-5)" }}>
            <div className="serial" style={{ marginBottom: "var(--s-3)" }}>Recording</div>
            {s.recording_r2_key
              ? <RecordingPlayer sessionId={s.id} />
              : <div className="muted">尚未上傳</div>}
            {s.metric_summary && (
              <details style={{ marginTop: "var(--s-3)" }}>
                <summary className="muted" style={{ fontSize: "var(--t-sm)", cursor: "pointer" }}>Metric summary JSON</summary>
                <pre style={{ fontSize: "var(--t-xs)", marginTop: "var(--s-2)", padding: "var(--s-3)", background: "var(--paper-deep)", borderRadius: "var(--r-sm)" }}>
                  {JSON.stringify(safeParse(s.metric_summary), null, 2)}
                </pre>
              </details>
            )}
          </div>
        </section>

        <section className="rise rise-7" style={{ marginBottom: "var(--s-7)" }}>
          <div className="section-head">
            <div>
              <div className="eyebrow">Session events · {events.length}</div>
              <h2>紀錄</h2>
            </div>
            {events.length > 0 && (
              <button className="btn btn--small btn--ghost" onClick={() => exportEventsCsv(events, s.id)}>
                匯出 CSV
              </button>
            )}
          </div>
          <EventTable events={events} />
        </section>

        <section className="rise rise-7">
          <div className="section-head">
            <div>
              <div className="eyebrow">Clinical notes · {notes.length}</div>
              <h2>訓練師筆記</h2>
            </div>
          </div>

          {/* New note form */}
          <form onSubmit={postNote} className="surface" style={{ padding: "var(--s-5)", marginBottom: "var(--s-5)" }}>
            <div className="field">
              <label className="field-label">寫一段觀察 / 建議</label>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="例：今日 alpha-uplift 訓練，個案中段疲勞明顯，建議下次縮為 25 分。"
              />
            </div>
            <div className="row row--between" style={{ marginTop: "var(--s-3)" }}>
              <label style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={visibleToCase} onChange={(e) => setVisibleToCase(e.target.checked)} />
                個案可見
              </label>
              {err && <div className="alert" style={{ flex: 1, marginRight: "var(--s-3)" }}>{err}</div>}
              <button type="submit" className="btn btn--rust btn--small" disabled={posting || !draft.trim()}>
                {posting ? "送出中…" : "送出筆記"}
              </button>
            </div>
          </form>

          {notes.length === 0 ? (
            <div className="empty muted">還沒寫筆記</div>
          ) : (
            <div className="col" style={{ gap: "var(--s-3)" }}>
              {notes.map(n => (
                <article key={n.id} className="surface" style={{ padding: "var(--s-5)" }}>
                  <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
                    <span className="serial">{n.created_at}</span>
                    {n.visible_to_case
                      ? <span className="chip chip--olive">個案可見</span>
                      : <span className="chip">僅內部</span>}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}>{n.note_md}</div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function StateChip({ state }: { state: string }) {
  if (state === "live")    return <span className="chip chip--olive">live</span>;
  if (state === "ended")   return <span className="chip">ended</span>;
  if (state === "aborted") return <span className="chip chip--danger">aborted</span>;
  if (state === "lobby")   return <span className="chip chip--petrol">lobby</span>;
  return <span className="chip chip--rust">{state}</span>;
}

function safeParse(s: string | null): any {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}
function fmtFull(s: string) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("zh-TW", { month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtTime(s: string) { return s.slice(11, 16); }

// ── Session events table ────────────────────────────────────────────
// Compact timeline of every event the DO logged this session.
// Per-row: timestamp | actor chip (rust/petrol/olive/default per role) |
// event type (mono) | payload (truncated). Long sessions get scroll-y
// to keep the page tractable.
function EventTable({ events }: { events: Event[] }) {
  if (events.length === 0) {
    return <div className="muted">這個 session 還沒有事件記錄。</div>;
  }
  return (
    <div className="surface" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--t-sm)" }}>
          <thead>
            <tr style={{ background: "var(--paper-warm)", position: "sticky", top: 0, zIndex: 1 }}>
              <th style={cellTh}>時間</th>
              <th style={cellTh}>來源</th>
              <th style={cellTh}>事件</th>
              <th style={cellTh}>內容</th>
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                <td style={{ ...cellTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)", whiteSpace: "nowrap" }}>
                  {ev.ts.slice(11, 19)}
                </td>
                <td style={cellTd}><ActorChip actor={ev.actor} /></td>
                <td style={{ ...cellTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)" }}>{ev.type}</td>
                <td style={{ ...cellTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>
                  {payloadPreview(ev.payload)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cellTh: CSSProperties = {
  textAlign: "left", padding: "var(--s-2) var(--s-3)",
  fontSize: "var(--t-xs)", letterSpacing: "0.08em",
  color: "var(--ink-soft)", fontWeight: 500, textTransform: "uppercase",
};
const cellTd: CSSProperties = { padding: "var(--s-2) var(--s-3)", verticalAlign: "top" };

function ActorChip({ actor }: { actor: "trainer" | "case" | "system" }) {
  if (actor === "trainer") return <span className="chip chip--rust" style={{ fontSize: "var(--t-xs)" }}>訓練師</span>;
  if (actor === "case")    return <span className="chip chip--olive" style={{ fontSize: "var(--t-xs)" }}>個案</span>;
  return <span className="chip" style={{ fontSize: "var(--t-xs)" }}>系統</span>;
}

function payloadPreview(p: string | null): string {
  if (!p) return "—";
  if (p.length <= 80) return p;
  return p.slice(0, 80) + "…";
}

// CSV export — RFC 4180 compatible quoting (double-quote any field
// containing commas, quotes, or newlines; escape internal quotes).
function exportEventsCsv(events: Event[], sid: string) {
  const rows = [
    ["id", "ts", "actor", "type", "payload"],
    ...events.map(e => [
      String(e.id), e.ts, e.actor, e.type, e.payload ?? "",
    ]),
  ];
  const csv = rows.map(r => r.map(csvField).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cognihome-events-${sid}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function csvField(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

// ── Join URL card ───────────────────────────────────────────────────
// Surfaces the join link for the trainer to send the case (copy or open
// QR). The same data is shown in the case-creation modal but disappears
// after one render — this card persists until the case activates.
function JoinUrlCard({ url, expiresAt, activated }: {
  url: string; expiresAt: string | null; activated: boolean;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* */ }
  }

  return (
    <div className="surface" style={{ marginTop: "var(--s-5)", padding: "var(--s-5)", borderColor: "var(--rust)" }}>
      <div className="row row--between" style={{ marginBottom: "var(--s-2)" }}>
        <div className="serial" style={{ color: "var(--rust)" }}>JOIN URL · 寄給個案</div>
        {activated && <span className="chip chip--olive" style={{ fontSize: "var(--t-xs)" }}>已活化</span>}
      </div>
      <div style={{
        fontFamily: "var(--mono)", fontSize: "var(--t-sm)", padding: "var(--s-3)",
        background: "var(--paper-deep)", borderRadius: "var(--r-sm)", wordBreak: "break-all",
      }}>{url}</div>
      <div className="row" style={{ marginTop: "var(--s-3)", gap: "var(--s-3)", alignItems: "center" }}>
        <button className="btn btn--small" onClick={copy}>
          {copied ? "已複製 ✓" : "複製連結"}
        </button>
        {expiresAt && (
          <span className="muted" style={{ fontSize: "var(--t-xs)" }}>
            到期：{expiresAt.slice(0, 16).replace("T", " ")}
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: "var(--t-xs)", marginTop: "var(--s-3)" }}>
        {activated
          ? "個案已用此連結加入過。需要換裝置或重新登入時可再次傳給個案。"
          : "個案在到期前用任何裝置打開這個連結就能加入此次 session。"}
      </div>
    </div>
  );
}

// ── Recording player ────────────────────────────────────────────────
// Worker streams the WebM bytes inline; we fetch with bearer auth into
// a Blob and bind to <video> via blob URL. Adequate for review-length
// sessions; for very long recordings we'd switch to MediaSource SE
// chunked playback (deferred — most CogniHome sessions are <60 min).
function RecordingPlayer({ sessionId }: { sessionId: string }) {
  const [url, setUrl]     = useState<string | null>(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bytes, setBytes] = useState(0);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  async function load() {
    setBusy(true); setError(null);
    try {
      const token = sessionStorage.getItem("cognihome_access_token");
      const r = await fetch(`${API_BASE}/api/sessions/${sessionId}/recording`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      setBytes(blob.size);
      setUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!url) {
    return (
      <div>
        <button onClick={load} disabled={busy} className="btn btn--ghost">
          {busy ? "載入中…" : "▶ 觀看 / 下載錄影"}
        </button>
        {error && <div className="alert" style={{ marginTop: "var(--s-3)" }}>{error}</div>}
      </div>
    );
  }
  return (
    <div>
      <video src={url} controls style={{ width: "100%", borderRadius: "var(--r-sm)", background: "#000" }} />
      <div className="row" style={{ gap: "var(--s-3)", marginTop: "var(--s-3)", alignItems: "center" }}>
        <a href={url} download={`cognihome-${sessionId}.webm`} className="btn btn--small btn--ghost">下載 .webm</a>
        <span className="muted tabular" style={{ fontSize: "var(--t-xs)" }}>{(bytes / 1024 / 1024).toFixed(1)} MB</span>
      </div>
    </div>
  );
}
